import type {
  ServerBaggageAvailabilityDefinition,
  ServerBaggageAvailabilityOffer,
  ServerBaggageAvailabilityResponse,
} from 'api/flights'
import { reportClientMessage } from 'services/errorReportingService'

// Lowest bag ordinal a single-select UI can sell. The airline files additional bags as an ordered
// sequence — VA's `0FA` is "1ST ADDITIONAL BAG", `0FB` is "2ND ADDITIONAL BAG" — so the second is only
// valid on top of the first, never on its own.
const FIRST_SELLABLE_BAGGAGE_OCCURRENCE = 1

// `-1` is the airline's "non-specific" ordinal: the bag is not part of a numbered sequence, so it is
// independently sellable.
const NON_SPECIFIC_BAGGAGE_OCCURRENCE = -1

/**
 * Whether this offer is sellable on its own.
 *
 * The modal is a single-select dropdown: one bag per traveller per leg, with no quantity control. It
 * therefore cannot express "a first bag AND a second bag", which is the only context in which a
 * second-occurrence product may be bought. Offering it anyway is worse than hiding it, on two counts:
 * the labels are weight-only, so `0FA` and `0FB` both render as "23kg" at the same price and are
 * indistinguishable; and picking the second one alone would ask the airline for a 2nd additional bag
 * that has no 1st.
 *
 * Offers missing the ordinal are kept, so a cached pre-deploy availability response still renders.
 *
 * Remove this filter when the modal gains a quantity control — at that point the higher occurrences
 * become the quantity-2..N options rather than separate rows, and they must be sent in ordinal order.
 */
function isIndependentlySellable(offer: ServerBaggageAvailabilityOffer): boolean {
  const occurrence = offer.baggageOccurrence?.first
  if (occurrence === undefined) { return true }
  return occurrence === FIRST_SELLABLE_BAGGAGE_OCCURRENCE ||
    occurrence === NON_SPECIFIC_BAGGAGE_OCCURRENCE
}

// Folds the raw `{ offerId, definitions[], offers[] }` availability response into a
// per-leg, per-traveller structure the baggage modal can render directly, while keeping
// everything the selection→`AncillariesForReservation` builder needs (offerId, subCode,
// airline, basePriceAmount, currency). Mirrors `seatsAvailabilityMap` in style.
//
// The fold key is the LEG, not a flight: one offer is quoted for a whole through-checked leg and
// carries every flight of it, so keying by a flight would keep the first and lose the rest.
export function baggageAvailabilityMap(
  serverResponse: ServerBaggageAvailabilityResponse,
): App.FlightsBaggageAvailability {
  const definitionsById = new Map<string, ServerBaggageAvailabilityDefinition>(
    (serverResponse.definitions ?? []).map(definition => [definition.id, definition]),
  )

  // legId -> travellerId -> options, preserving first-seen ordering.
  const legOrder: Array<string> = []
  const legsById = new Map<string, App.FlightsBaggageAvailabilityLeg>()

  let currency = ''
  let droppedForMissingBasePrice = 0

  for (const offer of serverResponse.offers ?? []) {
    const definition = definitionsById.get(offer.ancillaryDefinitionId)
    const option = mapOption(offer, definition)
    // Hide options with no KILO weight limit: the modal shows weight-only labels, so a
    // definition without a KILO weight has nothing meaningful to display.
    if (option.kg === undefined) { continue }
    // An offer with no BASE price is unsellable, not cheap. `basePriceAmount` is the only money
    // value forwarded to the airline, so substituting the tax-inclusive total would send a gross
    // amount as the base and the booking is rejected on a price mismatch — after payment. Drop it.
    if (!hasBasePrice(offer)) {
      droppedForMissingBasePrice++
      continue
    }
    // Expected on most airlines, so deliberately not reported: a second-bag product is dropped on
    // every VA availability call, and a per-request signal would be pure noise.
    if (!isIndependentlySellable(offer)) { continue }
    if (option.currency && !currency) {
      currency = option.currency
    }

    const legId = resolveLegId(offer)
    let leg = legsById.get(legId)
    if (!leg) {
      leg = {
        legId,
        isLegKeyed: offer.selectedFareId !== undefined,
        flightIds: [],
        origin: offer.origin,
        destination: offer.destination,
        travellers: [],
      }
      legsById.set(legId, leg)
      legOrder.push(legId)
    }

    for (const flightId of offerFlightIds(offer)) {
      if (!leg.flightIds.includes(flightId)) {
        leg.flightIds.push(flightId)
      }
    }

    for (const travellerId of offer.travelerIds ?? []) {
      let traveller = leg.travellers.find(entry => entry.travellerId === travellerId)
      if (!traveller) {
        traveller = { travellerId, options: [] }
        leg.travellers.push(traveller)
      }
      // Each offer/traveller pairing carries its own price, so clone per traveller.
      traveller.options.push({ ...option })
    }
  }

  if (droppedForMissingBasePrice > 0) {
    reportClientMessage(
      'flights_baggage_offer_missing_base_price',
      {
        offerId: serverResponse.offerId,
        droppedOfferCount: droppedForMissingBasePrice,
        totalOfferCount: (serverResponse.offers ?? []).length,
      },
      'warning',
      { domain: 'flights', surface: 'baggage_availability', check: 'offer_base_price_present' },
    )
  }

  return {
    offerId: serverResponse.offerId,
    currency,
    legs: legOrder.map(legId => legsById.get(legId) as App.FlightsBaggageAvailabilityLeg),
  }
}

/**
 * The leg an offer belongs to. Prefers the leg the response stamped on the offer; falls back to the
 * offer's flight, which is what keeps a checkout working across a deploy — the availability cache
 * outlives it, so an offer fetched minutes earlier carries no leg id at all.
 */
function resolveLegId(offer: ServerBaggageAvailabilityOffer): string {
  return offer.selectedFareId ?? offer.flightId
}

/** Every flight the offer covers. A cached pre-re-key offer has only the single deprecated field. */
function offerFlightIds(offer: ServerBaggageAvailabilityOffer): Array<string> {
  const flightIds = offer.flightIds ?? []
  return flightIds.length > 0 ? flightIds : [offer.flightId]
}

function hasBasePrice(offer: ServerBaggageAvailabilityOffer): boolean {
  return offer.price?.base?.amount !== undefined
}

function mapOption(
  offer: ServerBaggageAvailabilityOffer,
  definition: ServerBaggageAvailabilityDefinition | undefined,
): App.FlightsBaggageAvailabilityOption {
  const total = offer.price?.total ?? offer.price?.base
  const kg = getKiloWeight(definition)
  const subCode = definition?.subCode ?? ''

  return {
    id: offer.offerItemId ?? `${resolveLegId(offer)}:${subCode}`,
    definitionId: offer.ancillaryDefinitionId,
    subCode,
    airline: definition?.airline ?? '',
    label: kg !== undefined ? `${kg}kg` : '',
    kg,
    amount: total?.amount ?? 0,
    // Never falls back to the total: an offer without a base is dropped by the caller instead.
    basePriceAmount: offer.price?.base?.amount ?? 0,
    currency: total?.currency ?? offer.price?.base?.currency ?? '',
    displayOnly: offer.price?.displayOnly ?? false,
    unavailable: offer.price?.unavailable ?? false,
  }
}

function getKiloWeight(definition: ServerBaggageAvailabilityDefinition | undefined): number | undefined {
  const kiloLimit = definition?.weightLimits?.find(limit => limit.unit === 'KILO')
  return kiloLimit?.weight
}
